import {
  ForwardedRef,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { countries } from '../../../page-builder/data/country';
import { Country } from '../../../page-builder/interface/dataInterface';
import {
  Controller,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';
import {
  convertToSnakeCase,
  getBorderProps,
  getFontSize,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { IBorderValueProps } from '../../puck/reusable-props/Border';
import { PaddingType } from '../../puck/reusable-props/padding';
import { MarginType } from '../../puck/reusable-props/NewMargin';
import { IOutlineValueProps } from '../../puck/reusable-props/Outline';

export interface IPhoneValue {
  phoneCode?: string;
  phoneNumber?: string;
}

export interface IPhoneNumberProps {
  label?: string;
  error?: string;
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  selectProps: SelectHTMLAttributes<HTMLSelectElement>;
  className?: string;
  fieldHeight?: string;
  radiusBorder: IBorderValueProps;
  outline?: IOutlineValueProps;
  placeHolderColor?: string;
  currentDeviceType?: string;
  placeHolderFontSize?: string;
  placeHolderweight?: string;
  textColor?: {
    color: string;
    isInherit: boolean;
  };
  backgroundColor?: {
    color: string;
    isInherit: boolean;
  };
  header?: {
    text: string;
    hide: boolean;
    padding: PaddingType;
    margin: MarginType;
  };

  PlaceHolderTextAlign?: string;
  placeHolderFontFamily?: string;
  placeHolderLetterSpace?: string;
  placeholder?: string;
  uniqueID?: string;
}

const countryCodeList = countries.reduce(
  (filteredCountries: Country[], country: Country) => {
    return filteredCountries.some(c => c.phoneCode === country.phoneCode)
      ? filteredCountries
      : [...filteredCountries, country];
  },
  [],
);

export const PhoneNumber = forwardRef(function PhoneNumberInternal(
  {
    label,
    error,
    inputProps,
    selectProps,
    className = '',
    fieldHeight,
    radiusBorder,
    outline,
    placeHolderColor,
    placeholder,
    placeHolderFontSize,
    textColor,
    header,
    currentDeviceType,
    backgroundColor,
    placeHolderweight,
    PlaceHolderTextAlign,
    placeHolderFontFamily,
    placeHolderLetterSpace,
    uniqueID,
  }: IPhoneNumberProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const PhoneClassName = convertToSnakeCase(placeholder || '') + uniqueID;
  const [isInputFocused, setIsInputFocused] = useState(false);
  const forbiddenSymbolsRegex = /[^\x20-\x7E]/g;

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const input = e.currentTarget;
    const clipboardData = e.clipboardData.getData('text');
    const sanitizedData = clipboardData.replace(forbiddenSymbolsRegex, '');

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentValue = input.value;

    const newValue =
      currentValue.slice(0, start) + sanitizedData + currentValue.slice(end);

    // Update the form state with new value
    input.value = newValue;

    // Restore cursor position after update
    setTimeout(() => {
      input.setSelectionRange(
        start + sanitizedData.length,
        start + sanitizedData.length,
      );
    }, 0);
  };

  return (
    <div className="">
      {label && (
        <label
          htmlFor="PhoneNumber"
          className="block text-sm font-medium leading-6 text-gray-900">
          Phone Number
        </label>
      )}
      <div className={`${className} rounded-md relative text-editor-content`}>
        {header?.hide && (
          <div
            style={{
              ...getPaddingStyles(
                header?.padding,
                currentDeviceType || 'desktop',
              ),
              ...getNewMarginStyles(
                header.margin,
                currentDeviceType || 'desktop',
              ),
            }}
            className="text-sm mb-1 ml-2"
            dangerouslySetInnerHTML={{
              __html: header?.text,
            }}
          />
        )}
        <div
          className="relative"
          onFocus={() => {
            setIsInputFocused(true);
          }}
          onBlur={() => {
            setIsInputFocused(false);
          }}>
          <div className="absolute inset-y-0 left-0 flex items-center">
            <label htmlFor="phone_code" className="sr-only">
              Country
            </label>
            <select
              style={{
                height: fieldHeight
                  ? isNaN(+fieldHeight)
                    ? fieldHeight
                    : +fieldHeight
                  : '48px',
                color: textColor?.isInherit ? 'inherit' : textColor?.color,
              }}
              className="rounded-md border-0 bg-transparent py-0 pl-3 pr-7 text-gray-500 focus:outline-none focus:ring-inset focus:ring-indigo-500 sm:text-sm"
              {...selectProps}>
              {countryCodeList.map(country => (
                <option
                  key={country.phoneCode}
                  style={{
                    backgroundColor: backgroundColor?.isInherit
                      ? 'inherit'
                      : backgroundColor?.color,
                    color: textColor?.isInherit ? 'inherit' : textColor?.color,
                  }}>
                  +{country.phoneCode}
                </option>
              ))}
            </select>
          </div>
          <input
            style={{
              height: fieldHeight
                ? isNaN(+fieldHeight)
                  ? fieldHeight
                  : +fieldHeight
                : '48px',
              ...getBorderProps(
                isInputFocused && outline ? outline : radiusBorder,
                currentDeviceType || 'desktop',
              ),
              color: textColor?.isInherit ? 'inherit' : textColor?.color,
              backgroundColor: backgroundColor?.isInherit
                ? 'inherit'
                : backgroundColor?.color,
            }}
            onPaste={handlePaste}
            ref={ref}
            type="text"
            placeholder={placeholder}
            className={`pl-20 block w-full rounded-md text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-inset border-gray-300 focus:border-indigo-500 focus:ring-indigo-600 sm:text-sm sm:leading-6 custom-placeholder${PhoneClassName} ${isInputFocused ? outline?.borderStyle?.[currentDeviceType || 'desktop'] : radiusBorder?.borderStyle?.[currentDeviceType || 'desktop']}`}
            {...inputProps}
          />
        </div>
        {error && <div className="error-message">{error}</div>}
        <style>
          {`
            .custom-placeholder${PhoneClassName}::placeholder{
            color: ${placeHolderColor};
             font-size: ${getFontSize(placeHolderFontSize)};
             font-weight: ${placeHolderweight};
             text-align: ${PlaceHolderTextAlign};
             font-family: ${placeHolderFontFamily};
             letter-spacing:${placeHolderLetterSpace}px;
          }
          `}
        </style>
      </div>
    </div>
  );
});

interface IPhoneNumberControlProps extends IPhoneNumberProps {
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >;
  uniqueID?: string;
  phoneNumberName: string;
  phoneCodeName: string;
  fieldHeight?: string;
  radiusBorder: IBorderValueProps;
  outline?: IOutlineValueProps;
  className?: string;
  placeholder?: string;
  placeHolderColor?: string;
  placeHolderFontSize?: string;
  placeHolderweight?: string;
  PlaceHolderTextAlign?: string;
  placeHolderFontFamily?: string;
  currentDeviceType?: string;
  placeHolderLetterSpace?: string;
  placeholderlineHeight?: string;
  header?: {
    text: string;
    hide: boolean;
    padding: PaddingType;
    margin: MarginType;
  };
}

export const PhoneNumberControl = ({
  className,
  phoneNumberName,
  phoneCodeName,
  fieldHeight,
  radiusBorder,
  outline,
  rules,
  placeholder,
  placeHolderColor,
  placeHolderFontSize,
  textColor,
  header,
  currentDeviceType,
  backgroundColor,
  placeHolderweight,
  PlaceHolderTextAlign,
  placeHolderFontFamily,
  placeHolderLetterSpace,
  selectProps: { ...restSelectProps },
  inputProps: { ...restInputProps },
  uniqueID,
}: IPhoneNumberControlProps) => {
  const { setValue, watch } = useFormContext();

  useEffect(() => {
    const Code =
      countries?.find(d => d.code === watch('Shipping.CountryCode'))
        ?.phoneCode || '+1';
    setValue('phone_code', `+${Code}`);
  }, [watch('Shipping.CountryCode')]);
  return (
    <Controller
      name={phoneNumberName}
      render={({
        field: { value, onChange, ...restField },
        fieldState: { error },
      }) => {
        return (
          <PhoneNumber
            className={className}
            fieldHeight={fieldHeight}
            radiusBorder={radiusBorder}
            outline={outline}
            header={header}
            placeHolderColor={placeHolderColor}
            placeHolderFontSize={placeHolderFontSize}
            placeHolderweight={placeHolderweight}
            textColor={textColor}
            backgroundColor={backgroundColor}
            currentDeviceType={currentDeviceType}
            PlaceHolderTextAlign={PlaceHolderTextAlign}
            placeHolderFontFamily={placeHolderFontFamily}
            placeHolderLetterSpace={placeHolderLetterSpace}
            placeholder={placeholder}
            uniqueID={uniqueID}
            selectProps={{
              value: watch(phoneCodeName),
              onChange: e =>
                setValue(phoneCodeName, e.target.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                }),
              ...restSelectProps,
              color: '#000000',
            }}
            inputProps={{
              value: value,
              onChange: e =>
                onChange(
                  e.target.value?.replace(/[^\x20-\x7E]/g, '')?.trimStart(),
                ),
              ...restInputProps,
              ...restField,
            }}
            error={error?.message}
          />
        );
      }}
      rules={rules}
    />
  );
};
